import cn from "classnames";
import type { CSSProperties } from "react";

import { openSans } from "@/app/font";
import type {
  MedicalAlertSystemSection as MedicalAlertSystemSectionType,
  Theme,
} from "@/sanity.types";

import { CarouselCentered } from "../carouselCentered/carouselCentered.component";
import { RichText } from "../richText/richText.component";
import { MedicalAlertSystemItem } from "./medicalAlertSystemItem.component";

type Breakpoint = "base" | "md" | "lg";

// Full class names are spelled out so Tailwind can detect them at build time.
const THEME_BACKGROUND: Record<Breakpoint, Record<Theme, string>> = {
  base: {
    primary: "bg-primary",
    secondary: "bg-secondary",
    secondary100: "bg-secondary-100",
    tertiary: "bg-primary-100",
    light: "bg-greyscale-background",
    white: "bg-white",
    success: "bg-success",
    error: "bg-error",
    recommended: "bg-primary",
    promo: "bg-success",
  },
  md: {
    primary: "md:bg-primary",
    secondary: "md:bg-secondary",
    secondary100: "md:bg-secondary-100",
    tertiary: "md:bg-primary-100",
    light: "md:bg-greyscale-background",
    white: "md:bg-white",
    success: "md:bg-success",
    error: "md:bg-error",
    recommended: "md:bg-primary",
    promo: "md:bg-success",
  },
  lg: {
    primary: "lg:bg-primary",
    secondary: "lg:bg-secondary",
    secondary100: "lg:bg-secondary-100",
    tertiary: "lg:bg-primary-100",
    light: "lg:bg-greyscale-background",
    white: "lg:bg-white",
    success: "lg:bg-success",
    error: "lg:bg-error",
    recommended: "lg:bg-primary",
    promo: "lg:bg-success",
  },
};

const themeBackground = (
  theme: Theme | null | undefined,
  breakpoint: Breakpoint,
  fallback?: string,
) => (theme ? THEME_BACKGROUND[breakpoint][theme] : fallback);

type Spacing = MedicalAlertSystemSectionType["paddingY"];

/** CSS variables for one spacing value per screen size, or nothing when no value is set so the default classes apply. */
const spacingVariables = (
  name: string,
  desktop: Spacing,
  tablet: Spacing,
  mobile: Spacing,
  defaults: { mobile: number; tablet: number; desktop: number },
) =>
  desktop || tablet || mobile
    ? {
        [`--${name}`]: `${mobile ?? desktop ?? defaults.mobile}px`,
        [`--md-${name}`]: `${tablet ?? desktop ?? defaults.tablet}px`,
        [`--lg-${name}`]: `${desktop ?? defaults.desktop}px`,
      }
    : undefined;

const CARD_GAP = {
  default: "lg:gap-16",
  none: "lg:gap-0",
  sm: "lg:gap-2",
  md: "lg:gap-4",
  lg: "lg:gap-6",
};

export const MedicalAlertSystemSection = ({
  block,
}: MedicalAlertSystemSectionProps) => {
  const sectionClassName = cn(
    themeBackground(block.mobileSectionTheme, "base", "bg-primary-100"),
    themeBackground(
      block.tabletSectionTheme ?? block.mobileSectionTheme,
      "md",
      "md:bg-primary-100",
    ),
    themeBackground(block.sectionTheme, "lg", "lg:bg-primary-100"),
  );
  const paddingY = spacingVariables(
    "section-py",
    block.paddingY,
    block.tabletPaddingY,
    block.mobilePaddingY,
    { mobile: 40, tablet: 40, desktop: 40 },
  );
  const paddingX = spacingVariables(
    "section-px",
    block.paddingX,
    block.tabletPaddingX,
    block.mobilePaddingX,
    { mobile: 24, tablet: 64, desktop: 163 },
  );
  const contentGap = spacingVariables(
    "content-gap",
    block.contentGap,
    block.tabletContentGap,
    block.mobileContentGap,
    { mobile: 32, tablet: 32, desktop: 32 },
  );
  const cardPadding = spacingVariables(
    "card-padding",
    block.cardPadding,
    block.tabletCardPadding,
    block.mobileCardPadding,
    { mobile: 24, tablet: 24, desktop: 32 },
  );
  const cardImageGap = spacingVariables(
    "card-image-gap",
    block.cardImageGap,
    block.tabletCardImageGap,
    block.mobileCardImageGap,
    { mobile: 20, tablet: 20, desktop: 20 },
  );
  const cardTextGap = spacingVariables(
    "card-text-gap",
    block.cardTextGap,
    block.tabletCardTextGap,
    block.mobileCardTextGap,
    { mobile: 24, tablet: 24, desktop: 24 },
  );
  const contentClassName = cn(
    "mx-auto flex w-full max-w-[1440px] flex-col items-center",
    paddingY
      ? "py-(--section-py) md:py-(--md-section-py) lg:py-(--lg-section-py)"
      : "py-10",
    paddingX
      ? "px-(--section-px) md:px-(--md-section-px) lg:px-(--lg-section-px)"
      : "px-6 sm:px-10 md:px-16 lg:px-[163px]",
    contentGap
      ? "gap-(--content-gap) md:gap-(--md-content-gap) lg:gap-(--lg-content-gap)"
      : "gap-8",
  );
  const spacing = {
    ...paddingY,
    ...paddingX,
    ...contentGap,
    ...cardPadding,
    ...cardImageGap,
    ...cardTextGap,
  };
  const contentStyle = Object.keys(spacing).length
    ? (spacing as CSSProperties)
    : undefined;
  const cardGridClassName = cn(
    "hidden lg:grid lg:grid-cols-3",
    CARD_GAP[block.cardGap ?? "default"],
  );

  const renderItems = () => {
    if (!block.items || !block.items.length) return null;
    return block.items.map((item) => (
      <MedicalAlertSystemItem
        key={item._key}
        title={item.title}
        description={item.description}
        itemLegalDisclaimer={item.itemLegalDisclaimer}
        ctaLabel={item.ctaLabel}
        ctaLink={item.ctaLink}
        ctaTextColor={item.ctaTextColor}
        icon={item?.icon}
        iconSize={item.iconSize}
        tabletIconSize={item.tabletIconSize}
        mobileIconSize={item.mobileIconSize}
        className={cn(
          themeBackground(item.mobileTheme, "base", "bg-white"),
          themeBackground(
            item.tabletTheme ?? item.mobileTheme,
            "md",
            "md:bg-white",
          ),
          themeBackground(item.theme, "lg", "lg:bg-transparent"),
        )}
      />
    ));
  };

  return (
    <section
      data-testid="medical-alert-system-section"
      className={cn(openSans.className, "w-full", sectionClassName)}>
      <div className="container mx-auto">
        <div
          data-testid="medical-alert-system-content"
          className={contentClassName}
          style={contentStyle}>
          <div className="w-full pt-1 text-center">
            <RichText>{block.title}</RichText>
            <div className="mt-4">
              <RichText>{block.subTitle}</RichText>
            </div>
          </div>

          <div className="w-full">
            <div
              data-testid="medical-alert-system-grid"
              className={cardGridClassName}>
              {renderItems()}
            </div>

            {block.legalDisclaimer ? (
              <div
                data-testid="medical-alert-system-grid-disclaimer"
                className={cardGridClassName}>
                <div className="text-center">
                  <RichText>{block.legalDisclaimer}</RichText>
                </div>
              </div>
            ) : null}

            <div
              data-testid="medical-alert-system-carousel"
              className="lg:hidden">
              <CarouselCentered
                mobileSlidesPerView={1}
                mobileSpaceBetween={24}
                centeredSlides
                initialSlide={0}
                showNavigation
                equalSlideHeights
                slidesFooter={
                  <div className="mt-4 text-center">
                    <RichText>{block.legalDisclaimer}</RichText>
                  </div>
                }>
                {renderItems()}
              </CarouselCentered>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export type MedicalAlertSystemSectionProps = {
  block: MedicalAlertSystemSectionType;
};
